import { DatePicker, Flex, Select } from 'antd'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { organizationsTeamsListInfiniteOptions } from 'src/api/main'
import type {
  OrganizationsTeamsListData,
  PaginatedTeamListList,
  TeamList
} from 'src/api/main'
import { BadgeText, InfiniteSelect } from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import type { OptionTransformType } from 'src/utils'

import type { UserStatus, UserStatusFilter } from '../types'
import { STATUS_PALETTE } from '../utils/status'

const { RangePicker } = DatePicker

/** API value → the display state the status pill already uses for it. */
const STATUS_OPTIONS: { value: UserStatusFilter; display: UserStatus }[] = [
  { value: 'active', display: 'active' },
  { value: 'inactive', display: 'inactive' },
  { value: 'invited', display: 'invited' },
  { value: 'blocked', display: 'suspended' }
]

type ControlsProps = {
  team: number[]
  onTeamChange: (v: number[]) => void
  status?: UserStatusFilter
  onStatusChange: (v: UserStatusFilter | null) => void
  joinedRange: [Dayjs | null, Dayjs | null] | null
  onJoinedRangeChange: (v: [Dayjs | null, Dayjs | null] | null) => void
  workspaceId: number
}

export const UsersFilterControls = ({
  team,
  onTeamChange,
  status,
  onStatusChange,
  joinedRange,
  onJoinedRangeChange,
  workspaceId
}: ControlsProps) => {
  const { t } = useTranslation()

  return (
    <Flex align="center" gap={8} wrap="wrap">
      <Select<UserStatusFilter>
        size="small"
        allowClear
        placeholder={t('users.columns.status')}
        value={status}
        onChange={(value) => onStatusChange(value ?? null)}
        style={{ width: FILTER_CONTROL_WIDTH }}
        options={STATUS_OPTIONS.map(({ value, display }) => ({
          value,
          label: (
            <BadgeText
              text={t(`users.statuses.${display}`)}
              color={STATUS_PALETTE[display].dot}
            />
          )
        }))}
      />

      <InfiniteSelect<
        OrganizationsTeamsListData,
        PaginatedTeamListList,
        TeamList
      >
        size="small"
        mode="multiple"
        style={{ minWidth: 160, maxWidth: 280 }}
        allowClear
        value={team.length ? team : undefined}
        onChange={(v) =>
          onTeamChange(
            Array.isArray(v)
              ? v.filter((value): value is number => typeof value === 'number')
              : []
          )
        }
        getQueryFunction={organizationsTeamsListInfiniteOptions}
        // Membership lives on leaf teams only (API type `section`), so filter
        // server-side — otherwise the paginated pages return branches/
        // departments too and only a few options survive per page.
        baseOptions={{
          query: { organization: workspaceId, type: ['section'] }
        }}
        getOptions={({ options }: OptionTransformType<TeamList>) =>
          options.map((u) => ({ value: u.id, label: u.name }))
        }
        filterKey="team"
        placeholder={t('users.columns.teams')}
      />

      <RangePicker
        size="small"
        allowEmpty={[true, true]}
        style={{ width: 240 }}
        value={joinedRange}
        onChange={(next) =>
          onJoinedRangeChange(next as [Dayjs | null, Dayjs | null] | null)
        }
        placeholder={[
          t('users.filters.joinedFromPlaceholder'),
          t('users.filters.joinedToPlaceholder')
        ]}
        aria-label={t('users.filters.joinedChip')}
      />
    </Flex>
  )
}
